"use client";

import { useState } from "react";
import { BdCalculator } from "solar-icon-react/bd";
import { cn } from "@/lib";

type InflationResult = {
  equivalentValue: number;
  totalInflationPercent: number;
  averageAnnualInflationPercent: number;
  indexRatio: number;
  rows: InflationRow[];
};

type InflationRow = {
  year: number;
  index: number;
  yearlyInflation: number | null;
  equivalentValue: number;
  isEstimate?: boolean;
};

type Preset = {
  label: string;
  amount: number;
  fromYear: number;
  toYear: number;
};

const cpiByYear: Record<number, { index: number; isEstimate?: boolean }> = {
  1390: { index: 40.3 },
  1391: { index: 52.5 },
  1392: { index: 70.3 },
  1393: { index: 81.7 },
  1394: { index: 91.7 },
  1395: { index: 100 },
  1396: { index: 109.6 },
  1397: { index: 140.2 },
  1398: { index: 198.3 },
  1399: { index: 275.4 },
  1400: { index: 437 },
  1401: { index: 640.7 },
  1402: { index: 923.2 },
  1403: { index: 1174.5, isEstimate: true },
  1404: { index: 1445.8 },
};

const presets: Preset[] = [
  {
    label: "۱۰۰ میلیون از ۱۳۹۵ تا امروز",
    amount: 100_000_000,
    fromYear: 1395,
    toYear: 1404,
  },
  {
    label: "۵۰ میلیون از ۱۴۰۰",
    amount: 50_000_000,
    fromYear: 1400,
    toYear: 1404,
  },
  {
    label: "معکوس به ۱۳۹۵",
    amount: 1_000_000_000,
    fromYear: 1404,
    toYear: 1395,
  },
];

const years = Object.keys(cpiByYear)
  .map(Number)
  .sort((a, b) => a - b);

const toEnglishDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

const parseNumber = (value: string) =>
  Number(
    toEnglishDigits(value)
      .replace(/,/g, "")
      .replace(/[^\d.]/g, ""),
  ) || 0;

const formatNumber = (value: number) =>
  Math.round(value).toLocaleString("fa-IR");

const formatPercent = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) return "-";

  return `${value.toLocaleString("fa-IR", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}٪`;
};

const formatIndex = (value: number) =>
  value.toLocaleString("fa-IR", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  });

const buildRows = (
  amount: number,
  fromYear: number,
  toYear: number,
): InflationRow[] => {
  const start = Math.min(fromYear, toYear);
  const end = Math.max(fromYear, toYear);
  const originIndex = cpiByYear[fromYear].index;

  return years
    .filter((year) => year >= start && year <= end)
    .map((year) => {
      const previous = cpiByYear[year - 1]?.index;
      const current = cpiByYear[year].index;

      return {
        year,
        index: current,
        yearlyInflation: previous ? ((current / previous) - 1) * 100 : null,
        equivalentValue: amount * (current / originIndex),
        isEstimate: cpiByYear[year].isEstimate,
      };
    });
};

const calculateInflation = (
  amount: number,
  fromYear: number,
  toYear: number,
): InflationResult | null => {
  if (!amount || !cpiByYear[fromYear] || !cpiByYear[toYear]) {
    return null;
  }

  const indexRatio = cpiByYear[toYear].index / cpiByYear[fromYear].index;
  const yearsDiff = Math.abs(toYear - fromYear);
  const averageAnnualInflationPercent =
    yearsDiff > 0 ? (Math.pow(indexRatio, 1 / yearsDiff) - 1) * 100 : 0;

  return {
    equivalentValue: amount * indexRatio,
    totalInflationPercent: (indexRatio - 1) * 100,
    averageAnnualInflationPercent,
    indexRatio,
    rows: buildRows(amount, fromYear, toYear),
  };
};

const InflationCalculator = () => {
  const [amount, setAmount] = useState("");
  const [fromYear, setFromYear] = useState("1395");
  const [toYear, setToYear] = useState("1404");
  const [result, setResult] = useState<InflationResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  const handleCalculate = (
    nextAmount = amount,
    nextFromYear = fromYear,
    nextToYear = toYear,
  ) => {
    setHasCalculated(true);
    setResult(
      calculateInflation(
        parseNumber(nextAmount),
        Number(nextFromYear),
        Number(nextToYear),
      ),
    );
  };

  const handleMoneyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = parseNumber(event.target.value);
    setAmount(rawValue ? rawValue.toLocaleString("en-US") : "");
    setResult(null);
    setHasCalculated(false);
  };

  const applyPreset = (preset: Preset) => {
    const nextAmount = preset.amount.toLocaleString("en-US");
    const nextFromYear = String(preset.fromYear);
    const nextToYear = String(preset.toYear);

    setAmount(nextAmount);
    setFromYear(nextFromYear);
    setToYear(nextToYear);
    handleCalculate(nextAmount, nextFromYear, nextToYear);
  };

  return (
    <div className="relative w-full rounded-2xl bg-linear-to-b from-yellow-500 to-transparent p-[2px] shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:rounded-3xl">
      <div className="grid size-full items-stretch gap-5 rounded-[18px] bg-white p-3 sm:gap-6 sm:rounded-[22px] sm:p-4 lg:grid-cols-2 lg:p-6">
        <section className="order-1 flex h-full flex-col">
          <div className="border-primary-main/15 bg-primary-main/[0.03] mb-4 flex items-center gap-3 rounded-2xl border p-3 sm:p-4">
            <span className="bg-primary-main/10 text-primary-main flex size-10 shrink-0 items-center justify-center rounded-2xl sm:size-12">
              <BdCalculator size={30} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base leading-8 font-bold text-slate-900 sm:text-xl">
                ماشین حساب تورم
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                رایگان · آنلاین · بدون ثبت‌نام
              </p>
            </div>
          </div>

          <div className="border-primary-main/15 mb-4 rounded-2xl border p-4">
            <h3 className="font-bold text-slate-800">
              ارزش امروز پول سال‌های قبل
            </h3>
            <p className="mt-2 text-sm leading-7 text-slate-500">
              مبلغ را به تومان وارد کنید و با انتخاب سال مبدأ و مقصد، ارزش
              معادل پول، تورم کل دوره و میانگین تورم سالانه را ببینید.
            </p>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-2 min-[520px]:grid-cols-3">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => applyPreset(preset)}
                className="border-primary-main/15 text-primary-main hover:border-primary-main/35 hover:bg-primary-main/[0.03] min-h-12 rounded-xl border px-3 py-3 text-sm leading-6 font-bold transition"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 min-[520px]:grid-cols-2">
            <InputBlock
              label="مبلغ (تومان)"
              value={amount}
              onChange={handleMoneyChange}
              placeholder="مثلا 100,000,000"
            />
            <SelectBlock
              label="سال مبدأ"
              value={fromYear}
              onChange={(event) => {
                setFromYear(event.target.value);
                setResult(null);
                setHasCalculated(false);
              }}
            />
            <SelectBlock
              label="سال مقصد"
              value={toYear}
              onChange={(event) => {
                setToYear(event.target.value);
                setResult(null);
                setHasCalculated(false);
              }}
            />
            <div className="border-primary-main/10 flex h-full min-h-24 flex-col justify-center rounded-2xl border bg-white p-4">
              <p className="text-sm font-bold text-slate-700">مبنای محاسبه</p>
              <p className="mt-2 text-sm leading-7 text-slate-500">
                شاخص سالانه CPI با پایه ۱۳۹۵=۱۰۰
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleCalculate()}
            className="bg-primary-main mt-5 h-14 rounded-2xl text-base font-bold text-white shadow-[0_12px_28px_rgba(65,56,168,0.25)] transition hover:brightness-105"
          >
            محاسبه ارزش معادل
          </button>

          <div className="bg-primary-main/[0.03] mt-5 rounded-2xl p-3 text-sm leading-7 text-slate-600 sm:mt-auto sm:p-4">
            نتیجه این ابزار بر اساس نسبت شاخص سال مقصد به شاخص سال مبدأ محاسبه
            می‌شود و برای برآورد قدرت خرید کاربرد دارد. سال ۱۴۰۳ در جدول با
            داده علی‌الحساب نمایش داده می‌شود.
          </div>
        </section>

        <section className="border-primary-main/20 bg-primary-main/[0.02] order-2 flex h-full min-w-0 flex-col rounded-2xl border border-dashed p-3 sm:p-4">
          <h3 className="mb-2 text-lg font-bold text-slate-800">
            خروجی تورم همین‌جا ساخته می‌شود
          </h3>
          <p className="mb-5 text-sm leading-7 text-slate-600">
            پس از محاسبه، ارزش معادل پول، تورم تجمعی، میانگین سالانه و مسیر
            سال‌به‌سال شاخص نمایش داده می‌شود.
          </p>

          <div className="grid grid-cols-1 gap-3 min-[430px]:grid-cols-2">
            <SummaryCard label="ارزش معادل" value={result?.equivalentValue} />
            <SummaryCard
              label="تورم کل دوره"
              text={result ? formatPercent(result.totalInflationPercent) : "-"}
            />
            <SummaryCard
              label="میانگین سالانه"
              text={
                result
                  ? formatPercent(result.averageAnnualInflationPercent)
                  : "-"
              }
            />
            <SummaryCard
              label="نسبت تغییر شاخص"
              text={result ? `${formatIndex(result.indexRatio)} برابر` : "-"}
            />
          </div>

          {hasCalculated && !result && (
            <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm leading-7 text-amber-800">
              برای محاسبه، مبلغ را وارد کنید و سال مبدأ و مقصد را انتخاب کنید.
            </div>
          )}

          <div className="mt-5 min-h-[310px] flex-1 overflow-x-auto overflow-y-auto rounded-2xl border border-slate-100 bg-white">
            {result ? (
              <table className="w-full min-w-[560px] text-right text-xs">
                <thead className="sticky top-0 bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-3">سال</th>
                    <th className="p-3">شاخص CPI</th>
                    <th className="p-3">تورم سالانه</th>
                    <th className="p-3">ارزش معادل</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row) => (
                    <tr key={row.year} className="border-t border-slate-100">
                      <td className="p-3 font-bold">
                        {row.year.toLocaleString("fa-IR", {
                          useGrouping: false,
                        })}
                        {row.isEstimate ? "*" : ""}
                      </td>
                      <td className="p-3">{formatIndex(row.index)}</td>
                      <td
                        className={cn(
                          "p-3",
                          row.yearlyInflation !== null &&
                            row.yearlyInflation < 0 &&
                            "text-emerald-600",
                        )}
                      >
                        {formatPercent(row.yearlyInflation)}
                      </td>
                      <td className="p-3">
                        {formatNumber(row.equivalentValue)} تومان
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex h-full min-h-[310px] items-center justify-center p-6 text-center text-sm leading-7 text-slate-500">
                نتیجه پس از وارد کردن مبلغ و زدن دکمه محاسبه در این بخش نمایش
                داده می‌شود.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const InputBlock = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
}) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-slate-700">
      {label}
    </label>
    <div className="border-primary-main/10 focus-within:border-primary-main/45 h-14 w-full rounded-xl border bg-white px-4 transition">
      <input
        value={value}
        onChange={onChange}
        inputMode="numeric"
        placeholder={placeholder}
        className="size-full border-0 bg-transparent text-left text-sm font-bold text-slate-800 outline-none"
        dir="ltr"
      />
    </div>
  </div>
);

const SelectBlock = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-slate-700">
      {label}
    </label>
    <select
      value={value}
      onChange={onChange}
      className="border-primary-main/10 focus:border-primary-main/45 h-14 w-full rounded-xl border bg-white px-4 text-sm font-bold text-slate-800 outline-none"
    >
      {years.map((year) => (
        <option key={year} value={year}>
          {year.toLocaleString("fa-IR", { useGrouping: false })}
          {cpiByYear[year].isEstimate ? " (علی‌الحساب)" : ""}
        </option>
      ))}
    </select>
  </div>
);

const SummaryCard = ({
  label,
  value,
  text,
}: {
  label: string;
  value?: number;
  text?: string;
}) => (
  <div className="border-primary-main/10 rounded-xl border bg-white p-3 text-center">
    <p className="text-primary-main text-sm font-bold">{label}</p>
    <p className="mt-2 text-lg font-black text-slate-900">
      {text ?? (value ? `${formatNumber(value)} تومان` : "-")}
    </p>
  </div>
);

export default InflationCalculator;
